// IE Hub - Reusable Info Drawer/Dialog
// Responsive drawer for Terms, Privacy, Help, etc. Uses typed JSON content

import React, { useRef, useState } from 'react';
import {
  Box,
  CloseIcon,
  Dialog,
  DialogContent,
  Divider,
  Drawer,
  IconButton,
  Link,
  Slide,
  Stack,
  Typography,
  useMediaQuery,
  useTheme
} from '../ui';
import { TransitionProps } from '@mui/material/transitions';
import type { InfoContent } from '../shared/types/infoContent';

// Support both old structured format and new HTML format
export type InfoDrawerContent = InfoContent | {
  title: string;
  updatedAt?: string;
  htmlContent: string;
};

export interface InfoDrawerProps {
  open: boolean;
  onClose: () => void;
  content: InfoDrawerContent;
}

// Helper function to parse text, render **bold** segments and make URLs and emails clickable
export const parseTextWithLinks = (text: string): React.ReactNode => {
  const segments = text.split(/(\*\*[^*]+\*\*)/g);

  return segments.map((segment, index) => {
    if (!segment) return null;

    const boldMatch = segment.match(/^\*\*([^*]+)\*\*$/);
    if (boldMatch?.[1]) {
      return (
        <Box data-testid="components-parse-text-with-links-text-bold" key={index} component="strong" sx={{ fontWeight: 700, color: 'text.primary' }}>
          {parseLinks(boldMatch[1])}
        </Box>
      );
    }

    return <React.Fragment key={index}>{parseLinks(segment)}</React.Fragment>;
  });
};

// Helper function to make URLs and emails clickable
const parseLinks = (text: string): React.ReactNode => {
  // Combined regex to match URLs and email addresses
  const linkRegex = /(https?:\/\/[^\s]+|www\.[^\s]+|[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,})/g;
  const parts = text.split(linkRegex);
  
  return parts.map((part, index) => {
    if (!part) return null;
    
    // Check if this part is an email
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (part.match(emailRegex)) {
      return (
        <Link data-testid="components-parse-text-with-links-link-action-1"
          key={index}
          href={`mailto:${part}`}
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            wordBreak: 'break-word',
            '&:hover': {
              color: 'primary.dark'
            }
          }}
        >
          {part}
        </Link>
      );
    }
    
    // Check if this part is a URL
    const urlRegex = /^(https?:\/\/[^\s]+|www\.[^\s]+)$/;
    if (part.match(urlRegex)) {
      const href = part.startsWith('http') ? part : `https://${part}`;
      return (
        <Link data-testid="components-parse-text-with-links-link-action-2"
          key={index}
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            wordBreak: 'break-word',
            '&:hover': {
              color: 'primary.dark'
            }
          }}
        >
          {part}
        </Link>
      );
    }
    
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
};

const SlideUpTransition = React.forwardRef(function Transition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function InfoDrawer({ open, onClose, content }: InfoDrawerProps): JSX.Element {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [dragDistance, setDragDistance] = useState(0);
  const dialogRef = useRef<HTMLDivElement>(null);

  const minSwipeDistance = 50;

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientY);
    setIsDragging(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart) return;
    const currentTouch = e.targetTouches[0].clientY;
    const distance = currentTouch - touchStart;
    if (distance > 0) {
      setDragDistance(distance);
      setTouchEnd(currentTouch);
    }
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) {
      setIsDragging(false);
      setDragDistance(0);
      return;
    }
    const distance = touchEnd - touchStart;
    if (distance > minSwipeDistance) onClose();
    setTouchStart(null);
    setTouchEnd(null);
    setIsDragging(false);
    setDragDistance(0);
  };

  // Check if content is HTML format or structured format
  const isHtmlContent = 'htmlContent' in content;

  const body = (
    <Box data-testid="components-info-drawer-container-action-1" sx={{ height: '100%', display: 'flex', flexDirection: 'column', position: 'relative' }}>
      {/* Header */}
      <Box data-testid="components-info-drawer-container-action-2" sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        p: { xs: 2, md: 3 },
        minHeight: '64px'
      }}>
        <Stack data-testid="components-info-drawer-container-action-3" spacing={0.25}>
          <Typography data-testid="components-info-drawer-text-action-1" variant="h6" sx={{ fontWeight: 700, color: 'text.primary' }}>
            {content.title}
          </Typography>
          {content.updatedAt && (
            <Typography data-testid="components-info-drawer-text-updated" variant="caption" sx={{ color: 'text.secondary' }}>
              Updated {content.updatedAt}
            </Typography>
          )}
        </Stack>
        <IconButton data-testid="components-info-drawer-btn-on-close" onClick={onClose} sx={{ p: 1, backgroundColor: 'action.hover', '&:hover': { backgroundColor: 'action.selected' } }}>
          <CloseIcon />
        </IconButton>
      </Box>

      {/* Header divider — inset (mx) so the line doesn't touch the drawer borders, per Figma */}
      <Divider sx={{ mx: { xs: 2, md: 3 } }} />

      {/* Content */}
      <Box data-testid="components-info-drawer-container-action-4" sx={{ flex: 1, p: { xs: 2, md: 3 }, overflow: 'auto' }}>
        {isHtmlContent ? (
          // Render HTML content
          <Box data-testid="components-info-drawer-container-action-5" 
            dangerouslySetInnerHTML={{ __html: content.htmlContent }}
            sx={{
              '& h1': {
                fontSize: '1.5rem',
                fontWeight: 700,
                color: 'text.primary',
                mb: 2.5,
                mt: 0,
              },
              '& h2': {
                fontSize: '1rem',
                fontWeight: 700,
                color: 'primary.main',
                mb: 1.5,
                mt: 2.5,
              },
              '& p': {
                fontSize: '0.875rem',
                color: 'text.secondary',
                lineHeight: 1.7,
                mb: 1.5,
              },
              '& ol, & ul': {
                fontSize: '0.875rem',
                color: 'text.secondary',
                lineHeight: 1.7,
                mb: 1.5,
                pl: 2.5,
              },
              '& li': {
                mb: 0.75,
              },
              '& a': {
                color: 'primary.main',
                textDecoration: 'underline',
                '&:hover': {
                  color: 'primary.dark',
                },
              },
            }}
          />
        ) : (
          // Render structured content (old format)
          <Stack data-testid="components-info-drawer-container-action-6" spacing={3}>
            {content.sections.map((section, idx) => (
              <Box data-testid="components-info-drawer-container-action-7" key={idx}>
                {section.heading && (
                  <Typography data-testid="components-info-drawer-text-action-2" variant="subtitle1" sx={{ mb: 1, fontWeight: 700, color: 'primary.main' }}>
                    {section.heading}
                  </Typography>
                )}
                {section.body && (
                  <Typography data-testid="components-info-drawer-text-action-3" variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                    {parseTextWithLinks(section.body)}
                  </Typography>
                )}
                {section.link && (
                  <Box data-testid="components-info-drawer-container-action-8" sx={{ mt: 1.5 }}>
                    <Link data-testid="components-info-drawer-link-visit-our-website" 
                      href={section.link} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ 
                        color: 'primary.main',
                        textDecoration: 'underline',
                        fontSize: '0.875rem',
                        fontWeight: 500,
                        '&:hover': {
                          color: 'primary.dark'
                        }
                      }}
                    >
                      Visit our website
                    </Link>
                  </Box>
                )}
                {section.bullets && section.bullets.length > 0 && (
                  <Stack data-testid="components-info-drawer-container-action-9" component="ul" spacing={1} sx={{ mt: section.body ? 1.5 : 0, pl: 3, m: 0 }}>
                    {section.bullets.map((item, itemIdx) => (
                      <Typography data-testid="components-info-drawer-text-action-4" key={itemIdx} component="li" variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.7, whiteSpace: 'pre-line' }}>
                        {parseTextWithLinks(item)}
                      </Typography>
                    ))}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );

  if (isMobile) {
    return (
      <Dialog data-testid="components-info-drawer-modal-action"
        open={open}
        onClose={onClose}
        TransitionComponent={SlideUpTransition}
        fullScreen
        keepMounted
        sx={{
          // Ensure the privacy policy dialog always sits above any parent dialogs
          zIndex: (theme.zIndex?.modal ?? 1300) + 2,
          '& .MuiDialog-paper': {
            backgroundColor: 'background.default',
            backgroundImage: 'none',
            margin: 0,
            borderRadius: '16px 16px 0 0',
            transform: isDragging ? `translateY(${Math.min(dragDistance, 100)}px)` : 'none',
            transition: isDragging ? 'none' : 'transform 0.3s ease-out',
            opacity: isDragging ? Math.max(1 - dragDistance / 200, 0.7) : 1
          }
        }}
      >
        <DialogContent ref={dialogRef} sx={{ p: 0, height: '100%' }} onTouchStart={handleTouchStart} onTouchMove={handleTouchMove} onTouchEnd={handleTouchEnd}>
          <Box data-testid="components-info-drawer-container-action-10" sx={{ display: 'flex', justifyContent: 'center', pt: 1, pb: 0.5 }}>
            <Box data-testid="components-info-drawer-container-action-11" sx={{ width: 36, height: 4, backgroundColor: 'action.disabled', borderRadius: 2, opacity: 0.6 }} />
          </Box>
          {body}
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      keepMounted
      sx={{
        // Lift drawer above any underlying modals/dialogs so it is fully interactive
        zIndex: (theme.zIndex?.modal ?? 1300) + 1,
        '& .MuiDrawer-paper': {
          // Wider desktop drawer to improve readability
          width: { md: 'min(480px, 40vw)', lg: 'min(560px, 42vw)', xl: 'min(640px, 44vw)' },
          backgroundColor: 'background.paper',
          border: 'none',
          boxShadow: theme.shadows[8],
          borderRadius: '16px 0 0 16px'
        }
      }}
    >
      {body}
    </Drawer>
  );
}


